// ============================================================================
// apps/chatRenderer.tsx — ПРИЛОЖЕНИЕ «Чат» (рендерер appKind='chat').
// Чат — самостоятельное приложение: регистрирует РЕНДЕРЕР в реестре
// (registerAppRenderer); площадка оборачивает его в AppHost (изоляция:
// падение чата не роняет платформу/другие приложения; подписки через sdk).
// Чат по умолчанию занимает ВЕСЬ воркспейс (area='full').
// ============================================================================

import { useEffect, useState } from 'react';
import { registerAppRenderer, type AppSdk, type AppManifest, APPSDK_VERSION } from '@/lib/apps/sdk';
import { eventBusHub } from '@/lib/eventBusHub';

function ChatApp({ sdk }: { sdk: AppSdk }) {
  // ПРИЛОЖЕНИЕ «Чат» ПРЕДОСТАВЛЯЕТ возможность другим приложениям:
  //   действие object.discuss — «обсуждение объекта» (контекстные обсуждения).
  // Любое приложение вызывает sdk.apps.invoke('object', 'discuss', ctx) →
  // событие ui.open-context-chat → ContextChatProvider открывает панель.
  useEffect(() => {
    sdk.expose.registerAction('object', {
      code: 'discuss',
      name: 'Обсуждение',
      handler: async (payload) => {
        const p = (payload || {}) as { objectType?: string; objectCode?: string; objectId?: string; title?: string };
        if (!p.objectType || !p.objectId) {
          throw new Error('object.discuss: нужен {objectType, objectId}');
        }
        eventBusHub.emit('ui.open-context-chat', {
          objectType: p.objectType,
          objectCode: p.objectCode || '',
          objectId: p.objectId,
          title: p.title || '',
        });
      },
    });
  }, [sdk]);

  // TODO(P1): перевод UI ChatPage на AppSDK (sdk.events/navigation/notify) —
  // сейчас рендерится внутренний UI; данные/действия уже через ядро (chatBridge).
  const [Component, setComponent] = useState<React.ComponentType | null>(null);
  useEffect(() => {
    import('@/pages/ChatPage').then((m) => setComponent(() => m.ChatPage));
  }, []);
  if (!Component) return null;
  return <Component />;
}

/** Код системного приложения «Чат» (совпадает с kind='app' code='chat'). */
export const CHAT_APP_CODE = 'chat';

/** Манифест приложения «Чат» (для AppHost): системное, area=full.
 *  Чат доверенное системное приложение — все нужные скоупы (expose — чтобы
 *  предоставлять возможность object.discuss другим приложениям). */
export const CHAT_APP_MANIFEST: AppManifest = {
  code: CHAT_APP_CODE,
  name: 'chat',
  synonym: 'Чат',
  appKind: 'chat',
  area: 'full',
  apiVersion: APPSDK_VERSION,
  permissions: ['expose', 'messenger', 'notify', 'events', 'nav', 'data:read', 'data:write', 'files', 'settings:app'],
};

export function registerChatApp(): void {
  registerAppRenderer({
    appKind: 'chat',
    code: 'chat',
    title: 'Чат',
    Component: ChatApp,
    manifest: CHAT_APP_MANIFEST,
  });
}
